import React, { useEffect, useState } from "react";
import PortalLayout, { usePortal } from "../components/PortalLayout";
import KPI from "../components/KPI";
import PulseDot from "../components/PulseDot";
import DemoBadge from "../components/DemoBadge";
import { dashApi } from "../lib/api";
import { portalApi } from "../lib/portalApi";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Brain, FileBarChart, Upload } from "lucide-react";

const Inner = () => {
  const { me, orgs, activeOrg } = usePortal();
  const [summary, setSummary] = useState(null);
  const [reports, setReports] = useState([]);
  const [uploads, setUploads] = useState([]);

  useEffect(() => {
    dashApi.summary().then(setSummary);
    portalApi.listReports().then(r => setReports(r.reports || []));
    portalApi.listUploads().then(r => setUploads(r.uploads || []));
  }, []);

  const activeOrgObj = orgs.find(o => o.id === activeOrg);

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.28em] text-electric flex items-center gap-2">
            <PulseDot /> Portal Overview
          </div>
          <h1 className="font-display text-3xl md:text-4xl mt-2">Welcome back, {me?.name?.split(" ")[0]}</h1>
          <div className="text-sm text-cream-dim mt-1">
            Active organization: <span className="text-gold">{activeOrgObj?.name || "—"}</span>
            {activeOrgObj?.is_demo && <span className="ml-2"><DemoBadge /></span>}
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-4 gap-4">
        <KPI label="Entities Tracked" value={summary?.entities_tracked ?? "—"} testid="portal-kpi-entities" />
        <KPI label="Risk Flags" value={summary?.risk_flags_total ?? "—"} accent="warning" testid="portal-kpi-flags" />
        <KPI label="Your Reports" value={reports.length} testid="portal-kpi-reports" />
        <KPI label="Your Uploads" value={uploads.length} testid="portal-kpi-uploads" />
      </div>

      <div className="grid md:grid-cols-3 gap-5">
        <Link to="/portal/analyze" className="glass-accent p-6 hover:border-electric transition-colors" data-testid="portal-card-analyze">
          <Brain size={28} className="text-electric" />
          <div className="font-display text-xl mt-4">Run AI Analysis</div>
          <p className="text-sm text-cream-dim mt-2">Enter a public URL, upload a CSV, or run against demo data — get risk score, findings, and recommendations in seconds.</p>
        </Link>
        <Link to="/portal/reports" className="glass-accent p-6 hover:border-electric transition-colors" data-testid="portal-card-reports">
          <FileBarChart size={28} className="text-electric" />
          <div className="font-display text-xl mt-4">Generate Reports</div>
          <p className="text-sm text-cream-dim mt-2">Budget Audit · Infrastructure Risk · Citizen Impact · Cyber Readiness · Executive Summary. Export JSON / CSV / Markdown.</p>
        </Link>
        <Link to="/portal/uploads" className="glass-accent p-6 hover:border-electric transition-colors" data-testid="portal-card-uploads">
          <Upload size={28} className="text-electric" />
          <div className="font-display text-xl mt-4">Data Uploads</div>
          <p className="text-sm text-cream-dim mt-2">Budget PDFs, infrastructure CSVs, department records, water metrics — metadata catalogued per organization.</p>
        </Link>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        <div className="glass p-5">
          <div className="text-[11px] uppercase tracking-[0.22em] text-gold mb-3">Your permissions</div>
          <ul className="grid grid-cols-2 gap-2 text-sm" data-testid="portal-perms">
            {me && Object.entries(me.permissions || {}).map(([k, v]) => (
              <li key={k} className="flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${v ? "bg-[color:var(--auditiq-success)]" : "bg-steel"}`} />
                <span className="text-cream-dim">{k.replaceAll("_", " ")}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="glass p-5">
          <div className="text-[11px] uppercase tracking-[0.22em] text-gold mb-3">Organizations you can access</div>
          <ul className="text-sm space-y-2 max-h-60 overflow-auto" data-testid="portal-orgs-list">
            {orgs.map(o => (
              <li key={o.id} className="flex items-center justify-between border-b border-gold-dim/10 pb-2">
                <span className={o.id === activeOrg ? "text-gold" : "text-cream-dim"}>{o.name}</span>
                <span className="text-[10px] uppercase tracking-wider text-steel">{o.org_type}{o.is_demo && !/demo/i.test(o.name) ? " · demo" : ""}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

const PortalDashboard = () => <PortalLayout><Inner /></PortalLayout>;
export default PortalDashboard;
